/**
 * Ads Types Module
 * Centralized type definitions for Ads module (mock for now).
 */

export enum AdsAdvertiserStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  DISABLED = "DISABLED",
}

export enum AdsCampaignStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  ARCHIVED = "ARCHIVED",
  EXPIRED = "EXPIRED",
  COMPLETED = "COMPLETED",
}

export enum AdsPlacementKillSwitchStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
}

export type AdsPricingModel = "CPC" | "CPM" | "CPA" | "Flat";

export interface AdsPaginationParams {
  pageNum?: number;
  pageSize?: number;
}

export interface ListResponse<T> {
  results: T[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

export interface Advertiser {
  id: string;
  name: string;
  contactEmail: string;
  status: AdsAdvertiserStatus;
  createdAt: string; // ISO
}

export interface AdvertiserListParams extends AdsPaginationParams {
  search?: string;
  status?: AdsAdvertiserStatus | "ALL";
}

export interface CreateAdvertiserPayload {
  name: string;
  contactEmail: string;
}

export interface CreateAdvertiserResult {
  advertiser: Advertiser;
  message: string;
}

export interface AdCampaign {
  id: string;
  name: string;
  advertiserId: string;
  advertiserName: string;
  placement: string;
  pricingModel: AdsPricingModel;
  budget: number;
  spend: number;
  startAt: string; // ISO date
  endAt: string; // ISO date
  status: AdsCampaignStatus;
}

export interface AdCampaignListParams extends AdsPaginationParams {
  search?: string;
  placement?: string | "ALL";
  pricingModel?: AdsPricingModel | "ALL";
  status?: AdsCampaignStatus | "ALL";
  startAt?: string;
  endAt?: string;
  advertiserId?: string;
}

export interface Creative {
  id: string;
  campaignName: string;
  placement: string;
  status: AdsCampaignStatus;
  createdAt: string; // ISO
  previewUrl: string;
}

export interface CreativeListParams extends AdsPaginationParams {
  search?: string;
}

export interface Placement {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  frequencyCap: number;
  frequencyCapPerSession: boolean;
  maxAdvertisers: number;
}

export interface PlacementListParams extends AdsPaginationParams {
  search?: string;
}

export interface Report {
  id: string;
  title: string;
  reportType: "PERFORMANCE" | "REVENUE" | "AUDIT";
  status: AdsCampaignStatus;
  periodStartAt: string;
  periodEndAt: string;
}

export interface ReportListParams extends AdsPaginationParams {
  search?: string;
  reportType?: Report["reportType"] | "ALL";
}

export type BillingEventType = "CLICK" | "IMPRESSION" | "CPM" | "FLAT";

export interface BillingItem {
  id: string;
  advertiserName: string;
  campaignName: string;
  eventType: BillingEventType;
  referenceId: string;
  amount: number;
  timestamp: string; // ISO
}

export interface BillingListParams extends AdsPaginationParams {
  search?: string;
  advertiserId?: string;
}

export interface AdsAuditLogChange {
  field: string;
  old_value: any;
  new_value: any;
}

export interface AdsAuditLog {
  id: string;
  action: string;
  changedBy: {
    id: string;
    email: string;
    fullName: string;
  };
  changes: AdsAuditLogChange[];
  entityId: string;
  entityType: string;
  timestamp: string; // ISO
}

export interface AdsAuditLogListParams extends AdsPaginationParams {
  search?: string;
}

export interface CampaignBreakdownItem {
  campaignId: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  ctr: number; // %
  spend: number;
  conversions: number;
}

export interface ReportAnalytics {
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  totalAmountSpent: number;
  campaignBreakdown: CampaignBreakdownItem[];
}

export interface ReportAnalyticsParams {
  placement?: string;
  advertiserId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ---------------------------------------------------------------------------
// Ads "Create Campaign" Stepper (mock form types)
// ---------------------------------------------------------------------------

export interface AdCampaignPlacementOption {
  id: string;
  name: string;
  description: string;
  aspectRatio: string; // e.g. "16:9"
  placementRules: string[];
  creativeAspectHint: string; // e.g. "PNG, JPG up to 5MB • 1:1 (600×600)"
}

export const AD_PLACEMENT_OPTIONS: AdCampaignPlacementOption[] = [
  {
    id: "HOME_BANNER",
    name: "Home Banner",
    description: "Main homepage hero banner",
    aspectRatio: "16:9",
    placementRules: ["Max 1 per user per day", "Disable during live match option", "Frequency cap: 1 per user/day"],
    creativeAspectHint: "PNG, JPG up to 5MB • 1:1 (600×600)",
  },
  {
    id: "TOURNAMENT_BANNER",
    name: "Tournament Banner",
    description: "Banner displayed on tournament pages",
    aspectRatio: "4:1",
    placementRules: ["Max 1 per user per day", "Disable during live match option", "Frequency cap: 1 per user/day"],
    creativeAspectHint: "PNG, JPG up to 5MB • 1:1 (600×600)",
  },
  {
    id: "SHOP_BANNER",
    name: "Shop Banner",
    description: "Promotional banner in the shop section",
    aspectRatio: "3:1",
    placementRules: ["Max 1 per user per day", "Disable during live match option", "Frequency cap: 1 per user/day"],
    creativeAspectHint: "PNG, JPG up to 5MB • 1:1 (600×600)",
  },
  {
    id: "POPUP",
    name: "Popup",
    description: "Full-screen interstitial popup",
    aspectRatio: "1:1",
    placementRules: ["Max 1 per user per day", "Disable during live match option", "Frequency cap: 1 per user/day"],
    creativeAspectHint: "PNG, JPG up to 5MB • 1:1 (600×600)",
  },
];

// Geo targeting mock data (used by the Create Campaign targeting step)
export const MOCK_COUNTRIES: string[] = ["India"];

export const MOCK_STATES_BY_COUNTRY: Record<string, string[]> = {
  India: ["Maharashtra", "Karnataka", "Gujarat"],
};

export const MOCK_CITIES_BY_STATE: Record<string, string[]> = {
  Maharashtra: ["Mumbai", "Pune", "Thane"],
  Karnataka: ["Bangalore"],
  Gujarat: ["Ahmedabad"],
};

// ---------------------------------------------------------------------------
// Ads Overview
// ---------------------------------------------------------------------------

export interface AdsOverviewStat {
  value: number;
  pctChange: number;
}

export interface AdsOverviewStats {
  activeCampaigns: AdsOverviewStat;
  totalRevenueMTD: AdsOverviewStat;
  impressionsToday: AdsOverviewStat;
  clicksToday: AdsOverviewStat;
  ctrToday: AdsOverviewStat;
  activePopups: AdsOverviewStat;
}

export interface AdsBudgetAlertItem {
  name: string;
  advertiser: string;
  spent: number;
  budget: number;
  pct: number;
}

export interface AdsExpiringCampaignItem {
  name: string;
  advertiser: string;
  daysLeft: number;
  endsDate: string;
  urgent: boolean;
}

export interface AdsOverview {
  stats: AdsOverviewStats;
  budgetAlerts: AdsBudgetAlertItem[];
  expiringCampaigns: AdsExpiringCampaignItem[];
}

export type AdCampaignPricingModelUI = "FLAT" | "CPM" | "CPC";

export interface AdCampaignFormData {
  advertiserId: string;
  advertiserName: string;
  campaignName: string;
  pricingModel: AdCampaignPricingModelUI | "";
  /** Per-click / per-impression / flat-monthly rate, keyed by pricing model */
  pricingRate: number | "";
  dailyBudget: number | "";
  totalBudget: number | "";
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD

  placementId: string;
  placementName: string;

  creativeFile: File | null;
  creativePreviewUrl: string; // object URL (mock)

  ctaText: string;
  landingUrl: string;

  countries: string[];
  states: string[];
  cities: string[];

  pincodeCsvFile: File | null;
  pincodeCsvName: string;
}

export type AdCampaignValidationErrors = Partial<
  Record<
    | "advertiserId"
    | "campaignName"
    | "pricingModel"
    | "pricingRate"
    | "dailyBudget"
    | "totalBudget"
    | "startDate"
    | "endDate"
    | "placementId"
    | "ctaText"
    | "landingUrl"
    | "countries"
    | "creativeFile"
    | "pincodeCsvFile",
    string
  >
>;

export interface AdCampaignStepConfig {
  id: string;
  title: string;
  // The design for Ads create campaign shows "Step X of 5" with summary labeled as step 5.
  displayStepNumber: number;
  isCompleted: boolean;
}

// ---------------------------------------------------------------------------
// Create Ad
// ---------------------------------------------------------------------------

export type AdButtonRedirectionType =
  | "URL"
  | "Page"
  | "Custom"
  | "Academy"
  | "AcademyDetailsScreen"
  | "TournamentScreen"
  | "TournamentTab"
  | "WeeklyReport";

export interface AdButtonArrow {
  left: boolean;
  right: boolean;
}

export interface AdButton {
  category: string;
  label: string;
  color: string;
  textColor: string;
  titleColor: string;
  icon: string;
  iconPosition: string;
  image: string;
  redirectionType: AdButtonRedirectionType;
  url?: string;
  page?: string;
  customType?: string;
  customPath?: string;
  academyId?: string;
  academyName?: string;
  academyLocation?: string;
  coachingCenterId?: string;
  tournamentId?: string;
  tournamentName?: string;
  sports?: string;
  tournamentSeason?: string;
  seasonName?: string;
  tabName?: string;
  weeklyReportId?: string;
  weeklyReportName?: string;
  arrow: AdButtonArrow;
  desc: string;
}

export interface CreateAdCreative {
  type?: "IMAGE" | "VIDEO" | "GIF";
  media_url: string;
  thumbnail_url?: string;
  click_url: string;
  is_internal?: boolean;
  aspect_ratio?: string;
  duration?: number;
  platform_name?: string;
}

export interface CreateAdTargeting {
  tournament_ids?: string[];
  geo?: string[];
  platform?: string;
}

export interface CreateAdPayload {
  campaign_id: string;
  placement_id: string;
  placement_type: string;
  creative: CreateAdCreative;
  targeting?: CreateAdTargeting;
  banner_position?: number;
  buttons?: AdButton[];
  status?: string;
}

export interface CreateAdResult {
  id: string;
  message: string;
}

export interface UpdateAdPayload {
  creative?: Partial<CreateAdCreative>;
  targeting?: CreateAdTargeting;
  banner_position?: number;
  buttons?: AdButton[];
  status?: string;
}

// ---------------------------------------------------------------------------
// Ad Performance Analytics
// ---------------------------------------------------------------------------

export interface AdPerformanceDailyRow {
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface AdPerformanceSummary {
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  conversion_rate: number;
}

export interface AdPerformanceAnalytics {
  summary: AdPerformanceSummary;
  daily: AdPerformanceDailyRow[];
}

export interface AdPerformanceParams {
  adId: string;
  startDate: string;
  endDate: string;
}

